/**
 * Minimal SVG sparkline — no library, no axes, just the shape of growth.
 * Pass `data` as [{ day, impressions }, ...] (oldest first).
 *
 * Highlights the last point, draws a subtle area-fill underneath the line, and
 * shows the day labels (M, T, W, …) below.
 */
const DAYS_OF_WEEK = ["S", "M", "T", "W", "T", "F", "S"];

export default function Sparkline({ data = [], width = 280, height = 80, color = "#C6A87C", testId = "sparkline" }) {
  if (!data.length) return null;
  const values = data.map((d) => d.impressions || 0);
  const max = Math.max(1, ...values);
  const min = 0;
  const w = width;
  const h = height;
  const pad = 6;
  const innerW = w - pad * 2;
  const innerH = h - pad * 2;
  const x = (i) => pad + (i / Math.max(1, data.length - 1)) * innerW;
  const y = (v) => pad + (1 - (v - min) / (max - min)) * innerH;
  const points = data.map((d, i) => `${x(i).toFixed(1)},${y(d.impressions || 0).toFixed(1)}`);
  const linePath = `M ${points.join(" L ")}`;
  const areaPath = `${linePath} L ${x(data.length - 1).toFixed(1)},${(h - pad).toFixed(1)} L ${x(0).toFixed(1)},${(h - pad).toFixed(1)} Z`;

  const last = data[data.length - 1];
  const first = data[0];
  const delta = first.impressions > 0
    ? Math.round(((last.impressions - first.impressions) / first.impressions) * 100)
    : null;

  return (
    <div data-testid={testId}>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
        <defs>
          <linearGradient id={`spark-grad-${testId}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#spark-grad-${testId})`} />
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={x(data.length - 1)} cy={y(last.impressions || 0)} r="3.5" fill={color} stroke="#0A1D1A" strokeWidth="2" />
      </svg>
      <div className="flex justify-between items-center mt-1.5 text-[10px] text-white/45 tabular-nums" style={{ width: w }}>
        {data.map((d, i) => {
          const dow = new Date(d.day + "T00:00:00").getDay();
          return <span key={i}>{DAYS_OF_WEEK[dow]}</span>;
        })}
      </div>
      {delta !== null && (
        <p className="text-xs text-white/65 mt-2" data-testid={`${testId}-delta`}>
          <span className={delta >= 0 ? "text-[#7BC8A6]" : "text-rose-300"}>
            {delta >= 0 ? "↑" : "↓"} {Math.abs(delta)}%
          </span>{" "}
          <span className="text-white/45">vs. 7 days ago ({first.impressions.toLocaleString()} → {last.impressions.toLocaleString()})</span>
        </p>
      )}
    </div>
  );
}
